
import React, { useEffect, useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { FAQItem } from "@/types/faq";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorBoundary } from "@/components/ui/error-boundary";

export function FAQ() {
  const [faqItems, setFaqItems] = useState<FAQItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('faq_items')
          .select('*')
          .order('order', { ascending: true });
        
        if (error) {
          throw error;
        }
        
        setFaqItems(data || []);
      } catch (err: any) {
        console.error('Error fetching FAQ items:', err);
        setError('Unable to load FAQ items. Please try again later.');
        // Use default FAQs as fallback
        setFaqItems([
          {
            id: 'default-1',
            question: 'How does SparkQuotes work?',
            answer: "SparkQuotes sends you curated quotes directly to your phone based on your preferences and schedule. Simply set up your account, select your interests, and choose when you'd like to receive quotes.",
            order: 1
          },
          {
            id: 'default-2',
            question: 'Can I customize which type of quotes I receive?',
            answer: "Absolutely! You can select from a variety of categories including motivation, philosophy, success, mindfulness, and many more to ensure you receive quotes that resonate with you.",
            order: 2
          },
          {
            id: 'default-3',
            question: 'How many quotes will I receive?',
            answer: "You can customize the frequency of quotes you receive. Options range from daily quotes to multiple quotes throughout the day, or just a few per week - it's entirely up to you!",
            order: 3
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-4 border rounded-md">
              <Skeleton className="h-6 w-3/4 mb-4" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          ))}
        </div>
      );
    }

    if (faqItems.length > 0) {
      return (
        <Accordion type="single" collapsible className="w-full">
          {faqItems.map((item) => (
            <AccordionItem key={item.id} value={item.id}>
              <AccordionTrigger className="text-left font-medium">
                {item.question}
              </AccordionTrigger>
              <AccordionContent>
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      );
    }

    if (!error) {
      return <p className="text-center text-muted-foreground">No FAQ items available at this time.</p>;
    }

    return null;
  };

  return (
    <ErrorBoundary>
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
        
        {error && (
          <div className="p-4 mb-6 text-center text-amber-800 bg-amber-50 border border-amber-200 rounded-md">
            {error}
          </div>
        )}
        
        {renderContent()}
      </div>
    </ErrorBoundary>
  );
}
